import type { LocationType } from '../appointment-types';
import {
  CUSTOMER_ADDRESS_FALLBACK_TEXT,
  MEET_GENERIC_LINK_TEXT,
  MEET_LINK_FAILED_OWNER_NOTE,
  PHONE_CALL_WORDING,
  type BookingLocationAudience,
  type MeetLinkStatus,
} from './booking-location.constants';

export interface BookingLocationDescriptionInput {
  locationType?: LocationType | null;
  /** The booking's stored copy, already resolved (a phone is already formatted). */
  location?: string | null;
  meetLinkStatus?: MeetLinkStatus | null;
}

type LocationDescriber = (
  input: BookingLocationDescriptionInput,
  audience: BookingLocationAudience,
) => string | undefined;

/** `client`: the confirmed page and the client email. `owner`: the owner email. */
export function describeBookingLocation(
  input: BookingLocationDescriptionInput,
  audience: BookingLocationAudience,
): string | undefined {
  const describe = input.locationType
    ? LOCATION_DESCRIBERS[input.locationType]
    : undefined;
  return describe?.(input, audience);
}

const LOCATION_DESCRIBERS: Record<LocationType, LocationDescriber> = {
  none: () => undefined,
  google_meet: describeMeetLocation,
  in_person: ({ location }) => presentText(location),
  phone: describePhoneLocation,
  customer_address: describeCustomerAddressLocation,
  custom: ({ location }) => presentText(location),
};

function describeMeetLocation(
  { location, meetLinkStatus }: BookingLocationDescriptionInput,
  audience: BookingLocationAudience,
): string | undefined {
  const link = presentText(location);
  if (meetLinkStatus === 'ready' && link) return link;
  if (audience === 'client') return MEET_GENERIC_LINK_TEXT;
  return meetLinkStatus === 'failed' ? MEET_LINK_FAILED_OWNER_NOTE : undefined;
}

function describePhoneLocation(
  { location }: BookingLocationDescriptionInput,
  audience: BookingLocationAudience,
): string | undefined {
  const phoneNumber = presentText(location);
  return phoneNumber ? PHONE_CALL_WORDING[audience](phoneNumber) : undefined;
}

function describeCustomerAddressLocation(
  { location }: BookingLocationDescriptionInput,
  audience: BookingLocationAudience,
): string | undefined {
  const address = presentText(location);
  if (address) return address;
  return audience === 'client' ? CUSTOMER_ADDRESS_FALLBACK_TEXT : undefined;
}

function presentText(value: string | null | undefined): string | undefined {
  return value?.trim() ? value : undefined;
}
